import type { Message } from "@ai-sdk/react";
import type { ExternalStoreAdapter } from "@assistant-ui/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useChat } from "@ai-sdk/react";
import { AssistantRuntimeProvider, useRuntimeState } from "@assistant-ui/react";
import { useVercelUseChatRuntime } from "@assistant-ui/react-ai-sdk";
import { useQuery } from "@tanstack/react-query";

import { CodemodAIThread } from "@acme/ui/assistant-ui/thread";
import { ThreadContext } from "@acme/ui/assistant-ui/thread-context";

import { useTRPC, useTRPCClient } from "~/trpc/react";
import { useSession } from "~/utils/auth";
import { useCodemodAiPanel } from "../_hooks/use-codemod-ai-panel";
import { useStudioStore } from "../_hooks/use-studio-store";
import {
  AstGrepCodemodScriptResultToolUI,
  AstGrepCodeTransformationRuleResultToolUI,
  AstGrepSearchRuleResultToolUI,
} from "./ast-grep-result-tool";
import { CodemodAIHeader } from "./codemod-ai-header";
import { RateLimitMessage } from "./rate-limit-message";

interface CodemodAIProps {
  onClose: () => void;
}

function AstGrepAIRuntimeProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const [threadId, setThreadId] = useCodemodAiPanel();
  const language = useStudioStore((s) => s.lang);
  const currentYaml = useStudioStore((s) => s.config);
  const currentJavaScript = useStudioStore((s) => s.jsAstGrepCodemod);
  const threadIdToUse =
    threadId === "new" ? undefined : (threadId ?? undefined);

  const chat = useChat({
    api: "/api/ai/chat/studio",
    body: {
      language,
      currentYaml,
      currentJavaScript,
      threadId: threadIdToUse,
    },
    onResponse(response) {
      const newThreadId = response.headers.get("X-Thread-Id");
      if (newThreadId && newThreadId !== threadId) {
        void setThreadId(newThreadId);
      }
    },
    streamProtocol: "data",
  });

  const previousThreadId = useRef<string | undefined>(undefined);

  const { status: authStatus } = useSession();
  const api = useTRPC();
  const trpcClient = useTRPCClient();

  const { data: threads } = useQuery(
    api.aiConversation.listThreads.queryOptions(undefined, {
      enabled: authStatus === "authenticated",
    }),
  );

  const onSwitchToNewThread = useCallback(() => {
    void setThreadId("new");
  }, [setThreadId]);

  const onSwitchToThread = async (externalId: string) => {
    await setThreadId(externalId);
  };

  const setMessages = useCallback(
    (messages: Message[]) => {
      chat.setMessages(messages);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chat.setMessages],
  );

  const stop = useCallback(() => {
    chat.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.stop]);

  const switchToThread = useCallback(
    async (threadId: string) => {
      if (threadId === "new") {
        setMessages([]);
        return;
      }

      try {
        const messages =
          await trpcClient.aiConversation.getThreadMessages.query({
            threadId,
          });
        setMessages(messages);
      } catch (error) {
        console.error("Error fetching messages:", error);
        void setThreadId("new", {
          history: "replace",
        });
      }
    },
    [setMessages, setThreadId, trpcClient.aiConversation.getThreadMessages],
  );

  const normalizedThreads = useMemo(() => {
    return [
      ...(threads ?? []).map((t) => ({
        threadId: t.id,
        title: t.title ?? undefined,
        status: "regular" as const,
      })),
    ];
  }, [threads]);

  const threadList: NonNullable<
    ExternalStoreAdapter["adapters"]
  >["threadList"] = {
    threadId: threadId ?? undefined,
    onSwitchToNewThread,
    onSwitchToThread,
    threads: normalizedThreads,
  };

  const runtime = useVercelUseChatRuntime(chat, {
    adapters: {
      threadList,
    },
  });

  const isRunning = useRuntimeState(runtime.thread, (state) => state.isRunning);
  const isRunningNewRef = useRef(isRunning && threadId === "new");

  useEffect(() => {
    const threadIdWithUndefined =
      threadId === "new" ? undefined : (threadId ?? undefined);
    if (
      threadId &&
      authStatus === "authenticated" &&
      !isRunningNewRef.current &&
      previousThreadId.current !== threadIdWithUndefined
    ) {
      void switchToThread(threadId);
    } else {
      stop();
    }
    previousThreadId.current = threadIdWithUndefined;
  }, [threadId, switchToThread, authStatus, stop]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {children}
    </AssistantRuntimeProvider>
  );
}
export function CodemodAI({ onClose }: CodemodAIProps) {
  const { data: sessionData } = useSession();
  const api = useTRPC();
  const [threadId] = useCodemodAiPanel();

  const { data: rateLimitData } = useQuery(
    api.aiConversation.checkRateLimit.queryOptions(),
  );

  const isRateLimited = rateLimitData && !rateLimitData.success;
  const resetTime = isRateLimited ? rateLimitData.reset : null;

  if (isRateLimited && resetTime) {
    return <RateLimitMessage reset={resetTime} />;
  }

  return (
    <AstGrepAIRuntimeProvider>
      <AstGrepSearchRuleResultToolUI />
      <AstGrepCodeTransformationRuleResultToolUI />
      <AstGrepCodemodScriptResultToolUI />
      <ThreadContext.Provider
        value={{
          userAvatar: sessionData?.user.image ?? null,
          userName: sessionData?.user.name ?? null,
          threadId: threadId === "new" ? undefined : (threadId ?? undefined),
        }}
      >
        <CodemodAIThread
          composerClassName="px-4"
          contentClassName="px-4 pt-4"
          header={<CodemodAIHeader onClose={onClose} />}
        />
      </ThreadContext.Provider>
    </AstGrepAIRuntimeProvider>
  );
}
